import { Request } from 'commons/utils/request'
import { ButtonGroup, CircularProgress, DateTime, IconName, Layer, Link, Table } from 'ui/components'
import { Button } from 'ui/components/Buttons'
import { Page } from 'ui/components/Page'
import { ExportCSVButton } from 'ui/temp/ExportCSVButton'

import * as SC from './../../styles'
import { AddConnectionDialog, Filter } from './components'
import { useConnectionsList } from './hooks/useConnectionsList'
import { Connection } from './types'

export const ConnectionsList = () => {
  const {
    tableState,
    webPageData,
    isLoading,
    onSetWebPagePagination,
    onSetWebPageQuery,
    onSetWebPageOrder,
    onClear,
    isSetupKeyDialogOpen,
    openSetupKeyDialog,
    closeSetupKeyDialog,
    reloadData
  } = useConnectionsList()

  if (!webPageData) {
    return <CircularProgress centered />
  }

  return (
    <Page
      title="Connections"
      actions={
        <ButtonGroup margin="big">
          <ExportCSVButton
            reportName="License Connections"
            url={Request.urls.bank.connectionsExport}
            parametersForm={tableState.filterForm.config.value}
          />
          <Button face="tertiary" onClick={openSetupKeyDialog}>
            Add Connection
          </Button>
        </ButtonGroup>
      }
    >
      {isSetupKeyDialogOpen && (
        <AddConnectionDialog
          reloadData={reloadData}
          closeSetupKeyDialog={closeSetupKeyDialog}
          licensesToAssign={webPageData.licensesToAssign}
        />
      )}

      <Layer shadowed rounded>
        <Filter
          isDirty={tableState.filterForm.isDirty}
          isExpanded={tableState.expanded}
          form={tableState.filterForm}
          webPage={webPageData}
          onClear={onClear}
          onToggle={tableState.onFilterToggle}
          searchValue={tableState.filterForm.config.value.connectionsSearchQuery || ''}
          onSearch={onSetWebPageQuery}
        />
        <Table<Connection>
          loading={isLoading}
          columns={[
            {
              title: 'License',
              align: 'left',
              render: (item) => (
                <Link to="/licenses/$licenseId" params={{ licenseId: item.licenseId.toString() }}>
                  {item.licenseName} ({item.licenseNumber})
                </Link>
              )
            },
            {
              key: 'companyName',
              title: 'Relationship',
              align: 'left',
              sortable: true,
              render: (item) => (
                <Link to="/relationships/$id" params={{ id: item.companyId.toString() }}>
                  {item.companyName}
                </Link>
              )
            },
            {
              key: 'companyDBA',
              title: 'DBA',
              align: 'left'
            },
            {
              key: 'licenseState',
              title: 'State',
              align: 'left'
            },
            {
              key: 'lastConnectionDate',
              title: 'Last Connection Date',
              align: 'left',
              render: ({ lastConnectionDate }) => <DateTime date={lastConnectionDate} withTime />
            },
            {
              key: 'connectionType',
              title: 'Connection Type',
              align: 'left',
              render: ({ connectionType, licenseId }) => (
                <Link to={'/key-connections/$licenseId'} params={{ licenseId: licenseId.toString() }}>
                  {connectionType}
                </Link>
              )
            },
            {
              title: 'Status',
              width: '74px',
              render: ({ isConnected }) => (
                <>
                  {isConnected ? (
                    <SC.ConnectedIcon name={IconName.CHECK_ROUND} />
                  ) : (
                    <SC.NoConnectedIcon name={IconName.CROSS_ROUND} />
                  )}
                </>
              )
            }
          ]}
          dataSource={webPageData?.connections.map((el) => ({ item: el }))}
          dataQa={''}
          sorting={{
            order: tableState.order,
            onChange: onSetWebPageOrder
          }}
          pagination={{
            page: tableState.page,
            size: tableState.size,
            onChange: onSetWebPagePagination,
            total: webPageData.totalCount
          }}
        />
      </Layer>
    </Page>
  )
}

export default ConnectionsList
